/**
 * Effect Catalogue — single source of truth for ambient particle effects
 * used across the admin preview and the public viewer.
 *
 * The 15 effects are adapted from cinematic-particule-effect-stories-viewer.
 * Each effect is canvas-rendered for cinematic quality.
 *
 * Entry effects have been REMOVED - page transitions now serve as the
 * primary slide animation (integrated into the page transition system).
 *
 * Tier gating is enforced in three places:
 *  1. Backend validation rejects PRO effects on FREE slides/tiers
 *  2. The admin slide editor disables + labels the option when the
 *     current template tier is FREE
 *  3. The viewer renders the PRO effect only if the user's tier allows it
 */

export type EffectTier = 'FREE' | 'PRO';
export type EffectCategory = 'ambient';
export type SlideType = 'TITLE' | 'MESSAGE' | 'PHOTO' | 'MEMORY' | 'QUESTION' | 'FINAL';

export interface AmbientEffectMeta {
  code: string;
  label: string;
  tier: EffectTier;
  hint: string;
  slideTypes: SlideType[];
  recommended?: SlideType;
  order: number;
  accent: string;
}

const ALL_SLIDE_TYPES: SlideType[] = ['TITLE', 'MESSAGE', 'PHOTO', 'MEMORY', 'QUESTION', 'FINAL'];

/* ════════════════════════════════════════════════════════════════════════
   AMBIENT EFFECTS — 16 total: 15 canvas effects from
   cinematic-particule-effect-stories-viewer + the CSS-only
   `ambient-orbs` ported from why-merryfix_implementation (used as
   the deck-level default background).
   ════════════════════════════════════════════════════════════════════════ */
export const AMBIENT_EFFECTS: AmbientEffectMeta[] = [
  // ── FREE ────────────────────────────────────────────────────────
  {
    code: 'ambient-orbs',
    label: 'Ambient Orbs',
    tier: 'FREE',
    hint: 'Three drifting colour orbs from the marketing landing (CSS-only)',
    slideTypes: ALL_SLIDE_TYPES,
    recommended: 'TITLE',
    order: 5,
    accent: '#B7084E',
  },
  {
    code: 'floating-heart-bokeh',
    label: 'Floating Heart Bokeh',
    tier: 'FREE',
    hint: 'Soft hearts drift upward through champagne depth of field',
    slideTypes: ['TITLE', 'MESSAGE', 'QUESTION', 'FINAL'],
    recommended: 'QUESTION',
    order: 10,
    accent: '#FFB6C1',
  },
  {
    code: 'velvet-dust-motes',
    label: 'Velvet Dust Motes',
    tier: 'FREE',
    hint: 'Microscopic particles appear only when caught in a warm beam',
    slideTypes: ALL_SLIDE_TYPES,
    recommended: 'MEMORY',
    order: 20,
    accent: '#F0E68C',
  },
  {
    code: 'firefly-courtship',
    label: 'Firefly Courtship',
    tier: 'FREE',
    hint: 'Bioluminescent dots sync their pulses when they find a partner',
    slideTypes: ['MEMORY', 'MESSAGE', 'PHOTO'],
    recommended: 'MEMORY',
    order: 30,
    accent: '#CCFF00',
  },
  {
    code: 'champagne-bubble-rise',
    label: 'Champagne Bubble Rise',
    tier: 'FREE',
    hint: 'Rose-gold bubbles climb, wobble and pop at the surface',
    slideTypes: ['TITLE', 'FINAL', 'MESSAGE'],
    recommended: 'FINAL',
    order: 40,
    accent: '#F7E7CE',
  },

  // ── PRO ─────────────────────────────────────────────────────────
  {
    code: 'rose-petal-cascade',
    label: 'Rose Petal Cascade',
    tier: 'PRO',
    hint: 'Crimson petals tumble through warm air and fade at the floor',
    slideTypes: ['MESSAGE', 'QUESTION', 'FINAL'],
    recommended: 'QUESTION',
    order: 50,
    accent: '#8B0000',
  },
  {
    code: 'sparkler-trail-hearts',
    label: 'Sparkler Trail Hearts',
    tier: 'PRO',
    hint: 'Tap or swipe to throw glowing heart embers into the scene',
    slideTypes: ['TITLE', 'QUESTION', 'FINAL'],
    recommended: 'FINAL',
    order: 60,
    accent: '#FFBF00',
  },
  {
    code: 'constellation-connection',
    label: 'Constellation Connection',
    tier: 'PRO',
    hint: 'Nearby stars draw gold threads into a dissolving heart map',
    slideTypes: ['TITLE', 'MESSAGE', 'PHOTO'],
    recommended: 'TITLE',
    order: 70,
    accent: '#D4AF37',
  },
  {
    code: 'heartbeat-pulse-rings',
    label: 'Heartbeat Pulse Rings',
    tier: 'PRO',
    hint: 'Heart-shaped rings expand from a neon core like a romantic signal',
    slideTypes: ['FINAL', 'QUESTION'],
    recommended: 'FINAL',
    order: 80,
    accent: '#FF1493',
  },
  {
    code: 'silk-ribbon-flow',
    label: 'Silk Ribbon Flow',
    tier: 'PRO',
    hint: 'Translucent satin light weaves into a heart before unfurling',
    slideTypes: ['TITLE', 'FINAL', 'MESSAGE'],
    recommended: 'TITLE',
    order: 90,
    accent: '#E6B8B8',
  },
  {
    code: 'diamond-dust-sparkle',
    label: 'Diamond Dust Sparkle',
    tier: 'PRO',
    hint: 'Prismatic facets cluster around the center like jewelry in light',
    slideTypes: ALL_SLIDE_TYPES,
    recommended: 'PHOTO',
    order: 100,
    accent: '#B0E0E6',
  },
  {
    code: 'pheromone-trail',
    label: 'Pheromone Trail',
    tier: 'PRO',
    hint: 'Amber trails cross invisibly until intersections bloom peach',
    slideTypes: ['MESSAGE', 'QUESTION'],
    recommended: 'QUESTION',
    order: 110,
    accent: '#FFDAB9',
  },
  {
    code: 'aurora-heart',
    label: 'Aurora Heart',
    tier: 'PRO',
    hint: 'Curtains of color ripple until a heart emerges in the sky',
    slideTypes: ['TITLE', 'PHOTO', 'MEMORY'],
    recommended: 'PHOTO',
    order: 120,
    accent: '#39FF14',
  },
  {
    code: 'floating-kiss-marks',
    label: 'Floating Kiss Marks',
    tier: 'PRO',
    hint: 'Lipstick marks rise in different shades, rotations and opacity',
    slideTypes: ['MESSAGE', 'QUESTION', 'FINAL'],
    recommended: 'QUESTION',
    order: 130,
    accent: '#C41E3A',
  },
  {
    code: 'memory-fragment-drift',
    label: 'Memory Fragment Drift',
    tier: 'PRO',
    hint: 'Polaroid fragments pass the center, sharpen, then soften again',
    slideTypes: ['MEMORY', 'PHOTO'],
    recommended: 'MEMORY',
    order: 140,
    accent: '#F4E6CF',
  },
  {
    code: 'entwined-particle-vines',
    label: 'Entwined Particle Vines',
    tier: 'PRO',
    hint: 'Warm and cool streams grow together, then bloom where they meet',
    slideTypes: ['FINAL', 'QUESTION'],
    recommended: 'FINAL',
    order: 150,
    accent: '#FFD93D',
  },
  {
    code: 'cinematic-rain', label: 'Cinematic Rain', tier: 'PRO', hint: 'Motion-blur droplets with splash ripples.',
    slideTypes: ALL_SLIDE_TYPES, recommended: 'MESSAGE', order: 100, accent: '#8fbaff',
  },
  {
    code: 'velvet-snow', label: 'Velvet Snow', tier: 'PRO', hint: 'Soft flakes with layered parallax depth.',
    slideTypes: ALL_SLIDE_TYPES, recommended: 'MEMORY', order: 101, accent: '#c8d6f0',
  },
  {
    code: 'love-ember', label: 'Love Ember', tier: 'PRO', hint: 'Upward hearts with a warm glow decay.',
    slideTypes: ALL_SLIDE_TYPES, recommended: 'FINAL', order: 102, accent: '#ff5864',
  },
  {
    code: 'golden-hour-bokeh', label: 'Golden Hour Bokeh', tier: 'PRO', hint: 'Large soft circles with a screen blend.',
    slideTypes: ALL_SLIDE_TYPES, recommended: 'PHOTO', order: 103, accent: '#ffc46b',
  },
  {
    code: 'aurora-borealis', label: 'Aurora Borealis', tier: 'PRO', hint: 'Gradient bands with heavy blur morphing.',
    slideTypes: ALL_SLIDE_TYPES, recommended: 'PHOTO', order: 104, accent: '#42f5b3',
  },
  {
    code: 'floating-dust-motes', label: 'Floating Dust Motes', tier: 'PRO', hint: 'Brownian golden micro-particles in a light beam.',
    slideTypes: ALL_SLIDE_TYPES, recommended: 'MEMORY', order: 105, accent: '#ffe9b0',
  },
  {
    code: 'neon-city-haze', label: 'Neon City Haze', tier: 'PRO', hint: 'Vertical streaks with a colour-dodge haze.',
    slideTypes: ALL_SLIDE_TYPES, recommended: 'PHOTO', order: 106, accent: '#ff2878',
  },
  {
    code: 'ambient-fireflies', label: 'Fireflies', tier: 'PRO', hint: 'Blinking bioluminescent lights on drifting paths.',
    slideTypes: ALL_SLIDE_TYPES, recommended: 'MEMORY', order: 107, accent: '#dfff9a',
  },
  {
    code: 'steam-mist-rise', label: 'Steam / Mist Rise', tier: 'PRO', hint: 'Heavy blur blobs slowly rising through the scene.',
    slideTypes: ALL_SLIDE_TYPES, recommended: 'MESSAGE', order: 108, accent: '#c8e1eb',
  },
  {
    code: 'confetti-romance', label: 'Confetti Romance', tier: 'PRO', hint: 'Tumbling rose-gold shapes with 3D rotation.',
    slideTypes: ALL_SLIDE_TYPES, recommended: 'FINAL', order: 109, accent: '#ffb98a',
  },
  {
    code: 'starry-night-twinkle', label: 'Starry Night Twinkle', tier: 'PRO', hint: 'Twinkling stars with occasional shooting stars.',
    slideTypes: ALL_SLIDE_TYPES, recommended: 'TITLE', order: 110, accent: '#dfe8ff',
  },
  {
    code: 'sunset-gradient-shift', label: 'Sunset Gradient Shift', tier: 'PRO', hint: 'A slow twenty-second sunset colour morph.',
    slideTypes: ALL_SLIDE_TYPES, recommended: 'PHOTO', order: 111, accent: '#ff7854',
  },
  {
    code: 'water-caustics', label: 'Water Caustics', tier: 'PRO', hint: 'Turbulence overlays with a moving shimmer.',
    slideTypes: ALL_SLIDE_TYPES, recommended: 'PHOTO', order: 112, accent: '#7fe8ff',
  },
  {
    code: 'cherry-blossom-petals', label: 'Cherry Blossom Petals', tier: 'PRO', hint: 'Swaying petals tumble gently across the scene.',
    slideTypes: ALL_SLIDE_TYPES, recommended: 'MESSAGE', order: 113, accent: '#ffd7e6',
  },
  {
    code: 'candle-flicker-glow', label: 'Candle Flicker Glow', tier: 'PRO', hint: 'A radial warm pulse with rising sparks.',
    slideTypes: ALL_SLIDE_TYPES, recommended: 'FINAL', order: 114, accent: '#ffc46e',
  },
  {
    code: 'magic-sparkle-trail', label: 'Magic Sparkle Trail', tier: 'PRO', hint: 'Pop-in star particles in a magical trail.',
    slideTypes: ALL_SLIDE_TYPES, recommended: 'FINAL', order: 115, accent: '#ffd1ff',
  },
  {
    code: 'foggy-window', label: 'Foggy Window', tier: 'PRO', hint: 'Blurred masks with trickling condensation drops.',
    slideTypes: ALL_SLIDE_TYPES, recommended: 'MEMORY', order: 116, accent: '#d7ebf5',
  },
  {
    code: 'holographic-iridescence', label: 'Holographic Iridescence', tier: 'PRO', hint: 'Hue-rotating mesh gradients with a moving sheen.',
    slideTypes: ALL_SLIDE_TYPES, recommended: 'PHOTO', order: 117, accent: '#ff4d9d',
  },
  {
    code: 'heartbeat-pulse', label: 'Heartbeat Pulse', tier: 'PRO', hint: 'A rhythmic red vignette with expanding rings.',
    slideTypes: ALL_SLIDE_TYPES, recommended: 'FINAL', order: 118, accent: '#ff3248',
  },
  {
    code: 'floating-lanterns', label: 'Floating Lanterns', tier: 'PRO', hint: 'Glowing lanterns drift upward among twinkling stars.',
    slideTypes: ALL_SLIDE_TYPES, recommended: 'FINAL', order: 119, accent: '#ffb347',
  },

];

/** Lookup helpers used everywhere a code needs to be resolved.
 *  Uses a module-level `LIVE_EFFECTS` cache so the admin previewer
 *  sees the latest admin-managed catalogue as soon as the hook runs. */
let LIVE_EFFECTS: AmbientEffectMeta[] = AMBIENT_EFFECTS;

export function setLiveAmbientEffects(rows: AmbientEffectMeta[]) {
  if (rows.length > 0) LIVE_EFFECTS = rows;
}

function lookup(code: string): AmbientEffectMeta | null {
  return LIVE_EFFECTS.find((e) => e.code === code) ?? null;
}

export function getAmbientEffect(code: string | null | undefined): AmbientEffectMeta | null {
  if (!code) return null;
  return lookup(code);
}

/** Return the ambient effects that work for a given slide type, sorted by order. */
export function getAmbientEffectsForSlide(slideType: SlideType): AmbientEffectMeta[] {
  return LIVE_EFFECTS
    .filter((e) => e.slideTypes.includes(slideType))
    .slice()
    .sort((a, b) => a.order - b.order);
}

/** Did the user pick a code that's incompatible with the current slide type? */
export function isEffectCompatible(
  code: string | null | undefined,
  kind: 'ambient',
  slideType: SlideType,
): boolean {
  if (!code) return true;
  const meta = kind === 'ambient' ? getAmbientEffect(code) : null;
  if (!meta) return true;
  return meta.slideTypes.includes(slideType);
}

export function resolveAmbientEffect(
  code: string | null | undefined,
  tier: EffectTier,
  slideType?: SlideType,
): AmbientEffectMeta {
  let meta = getAmbientEffect(code);
  if (meta && slideType && !meta.slideTypes.includes(slideType)) {
    meta = getAmbientEffectsForSlide(slideType).find((m) => m.tier === 'FREE' && m.code !== 'none') ?? null;
  }
  if (meta && (tier === 'PRO' || meta.tier === 'FREE')) return meta;
  return getAmbientEffect('none')!;
}

export function tierToEffectTier(value: string | null | undefined): EffectTier {
  return value === 'PRO' ? 'PRO' : 'FREE';
}
